import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import type { JSX, KeyboardEvent, MouseEvent, ReactNode } from "react";
import { OPEN_LINK_EDITOR_COMMAND } from "../features/links/commands";
import type { BlockType } from "../index";
import { useComposerDispatch, useComposerState } from "./composer-context";
import type { InlineFormat } from "./handle";
import { Icon, type IconName } from "./icons";
import type { EditorLabels } from "./labels";

export interface ComposerToolbarProps {
  labels: EditorLabels;
  /** The host's controls, at the end of the row. */
  end: ReactNode;
}

type LabelKey = "bold" | "italic" | "strikethrough" | "code";
const MARKS: readonly { format: InlineFormat & LabelKey; icon: IconName }[] = [
  { format: "bold", icon: "bold" },
  { format: "italic", icon: "italic" },
  { format: "strikethrough", icon: "strikethrough" },
];

type BlockLabel = "numberedList" | "bulletedList" | "todoList";
const LISTS: readonly { block: BlockType; icon: IconName; label: BlockLabel }[] = [
  { block: "number", icon: "listNumbered", label: "numberedList" },
  { block: "bullet", icon: "listBulleted", label: "bulletedList" },
  { block: "check", icon: "listCheck", label: "todoList" },
];

// Buttons act on the editor's selection, so pressing one must not move focus out of it.
const keepSelection = (event: MouseEvent): void => event.preventDefault();

/** A press between two buttons is not a reason to leave the text. The host's own controls decide for themselves. */
const keepGaps = (event: MouseEvent): void => {
  if ((event.target as Element).closest(".ze-composer-toolbar-end") === null)
    event.preventDefault();
};

const FOCUSABLE = "button, a[href], input, select, textarea, [tabindex]";

/**
 * The composer's formatting row: every mark and block its preset offers, pressed where the
 * selection is in one. One Tab stop; the arrow keys move along it, the host's controls included.
 */
export function ComposerToolbar({ labels, end }: ComposerToolbarProps): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const state = useComposerState();
  const dispatch = useComposerDispatch();

  /** Runs with the keyboard in the text, where what the button did shows and typing carries on. */
  const act = (run: () => void): void => {
    if (!state.editable) return;
    const root = editor.getRootElement();
    if (root !== null && root.ownerDocument.activeElement !== root) dispatch({ type: "focus" });
    run();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      dispatch({ type: "focus" });
      return;
    }
    const rtl = event.currentTarget.matches(":dir(rtl)");
    const moves: Record<string, number> = { ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1 };
    const move = moves[event.key];
    if (move === undefined && event.key !== "Home" && event.key !== "End") return;
    const target = event.target as HTMLElement;
    // A text field of the host's keeps its arrow keys.
    if (target.matches("input, textarea, select")) return;
    const stops = [...event.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (stop) => !stop.matches(":disabled"),
    );
    const at = stops.indexOf(target);
    if (at === -1) return;
    event.preventDefault();
    const next =
      move === undefined
        ? event.key === "Home"
          ? 0
          : stops.length - 1
        : (at + move + stops.length) % stops.length;
    stops[next]?.focus();
  };

  const button = (options: {
    key: string;
    label: string;
    icon: IconName;
    pressed: boolean;
    enabled: boolean;
    first?: boolean;
    run: () => void;
  }): JSX.Element => (
    <button
      key={options.key}
      type="button"
      className="ze-tb-button"
      aria-label={options.label}
      aria-pressed={options.pressed}
      // Not `disabled`: the row stays one Tab stop, and keeps its place, whatever the selection is.
      aria-disabled={options.enabled && state.editable ? undefined : true}
      title={options.label}
      tabIndex={options.first === true ? 0 : -1}
      onMouseDown={keepSelection}
      onClick={() => {
        if (options.enabled) act(options.run);
      }}
    >
      <Icon name={options.icon} />
    </button>
  );

  const blockButton = (block: BlockType, icon: IconName, label: string): JSX.Element =>
    button({
      key: `block-${block}`,
      label,
      icon,
      pressed: state.block === block,
      enabled: true,
      // Asking for the block it already is would make it again. Pressed, the button takes it back.
      run: () =>
        dispatch({ type: "set-block", block: state.block === block ? "paragraph" : block }),
    });

  return (
    // oxlint-disable-next-line jsx-a11y/interactive-supports-focus -- its buttons take focus, as in the ARIA toolbar pattern
    <div
      className="ze-composer-toolbar"
      role="toolbar"
      aria-label={labels.composerToolbar}
      onKeyDown={onKeyDown}
      onMouseDown={keepGaps}
    >
      {MARKS.map(({ format, icon }, index) =>
        button({
          key: format,
          label: labels[format],
          icon,
          pressed: state[format],
          enabled: state.canFormat,
          first: index === 0,
          run: () => dispatch({ type: "format-text", format }),
        }),
      )}
      <span className="ze-tb-separator" aria-hidden="true" />
      {button({
        key: "link",
        label: labels.link,
        icon: "link",
        pressed: state.link !== null,
        enabled: state.canLink,
        run: () => editor.dispatchCommand(OPEN_LINK_EDITOR_COMMAND, undefined),
      })}
      {LISTS.map(({ block, icon, label }) => blockButton(block, icon, labels[label]))}
      <span className="ze-tb-separator" aria-hidden="true" />
      {button({
        key: "code",
        label: labels.code,
        icon: "code",
        pressed: state.code,
        enabled: state.canFormat,
        run: () => dispatch({ type: "format-text", format: "code" }),
      })}
      {blockButton("code", "codeBlock", labels.codeBlock)}
      {end !== undefined && end !== null && end !== false && (
        <div className="ze-composer-toolbar-end">{end}</div>
      )}
    </div>
  );
}
